import { z } from "zod";
import { enforceVisibilityInvariant, sha256Schema, tenantOwnedShape, visibilityShape, } from "./common.js";
import { sha256Hex } from "./document.js";
import { connectionIdSchema, documentIdSchema, sourceItemIdSchema, } from "./ids.js";
/**
 * A conversation rollup is one derived Markdown view of one chat window. The
 * message documents underneath it stay canonical and immutable; the rollup is a
 * composition of them that can be thrown away and rebuilt from scratch.
 *
 * This module owns only its *identity*: which window a message belongs to, what
 * the rollup of that window is called, and what digest says whether it is still
 * current. Nothing here writes, reads or renders a rollup.
 *
 * Every derivation is deterministic and re-derivable from the message
 * canonicals alone. That is the property the whole design rests on — a rollup
 * whose identity depended on when it was built could not be rebuilt, and
 * rebuilding is how a recalibrated window, a corrected message or a content
 * removal reaches the derived layer.
 */
/**
 * The field separator, written as an escape.
 *
 * U+001F is the unit separator this repository already joins deterministic keys
 * with. It is written `"\u001f"` rather than as a literal byte on purpose: a raw
 * separator is invisible in review and in a diff, and an accidentally deleted
 * one reads as two fields having been concatenated rather than as a bug. The
 * tests assert it at the byte level for the same reason.
 *
 * It is safe as a separator because no part that goes into a key may contain a
 * C0 control character — `conversationKeyPartSchema` rejects them, on the same
 * terms as the WhatsApp provider ids in `source.ts` — so no part can ever
 * impersonate a field boundary.
 */
export const CONVERSATION_KEY_SEPARATOR = "\u001f";
/**
 * The namespaces the two derived identities are salted with.
 *
 * Separate constants rather than one, because the two ids are derived from the
 * same parts: without distinct namespaces a window's source item and its rollup
 * document would receive the same UUID. They live in different tables so a
 * collision would be harmless, but an id that appears in two roles is an id
 * somebody will eventually match on across them.
 *
 * The `v1` suffix is the migration handle. Changing what goes into a key
 * changes every rollup's identity, so a future shape takes a new namespace and
 * re-derives rather than editing this one in place.
 */
export const CONVERSATION_ROLLUP_NAMESPACE = "obsina-conversation-rollup-v1";
export const CONVERSATION_WINDOW_NAMESPACE = "obsina-conversation-window-v1";
/**
 * The channels a conversation can arrive on.
 *
 * Not the provider: a Microsoft connection carries mail, calendar and Teams,
 * and only the Teams lane is a conversation. The channel is what separates a
 * Teams chat from the rest of the same connection, which is why it is in the
 * key even though the connection already is.
 */
export const conversationChannels = ["whatsapp", "teams"];
export const conversationChannelSchema = z.enum(conversationChannels);
/**
 * The window's character ceiling.
 *
 * Derived rather than chosen: 20 943 characters is the p90 of today's canonical
 * documents, so a window at this size is a document shape the system already
 * carries end to end. At the measured 316 characters per message that is about
 * 66 messages — but the cap is applied *in characters*, because a window
 * carrying images is heavier per message than one carrying text and a message
 * count would let it through anyway.
 *
 * PROVISIONAL, AND KNOWN TO BE. The number comes from today's mail and file
 * corpus because there is no chat corpus to measure yet. When real chat volume
 * exists the cap should be recalibrated — which is possible precisely because
 * the rollup is derived: every window is rebuilt under the new cap. It is not
 * pinned in a database CHECK for that reason.
 */
export const CONVERSATION_WINDOW_CHARACTER_CAP = 21_000;
// eslint-disable-next-line no-control-regex -- these parts form U+001F keys
const controlCharacterPattern = /^[^\u0000-\u001f]+$/u;
/**
 * A part that may be joined into a key: non-empty, bounded, and free of the
 * control characters the separator is drawn from.
 */
export const conversationKeyPartSchema = z
    .string()
    .min(1)
    .max(512)
    .regex(controlCharacterPattern, "must not contain C0 control characters");
/**
 * `YYYY-MM-DD#n`, where the day is the tenant's local calendar day and `n` is
 * the sub-window ordinal starting at 1.
 *
 * THE ORDINAL IS ALWAYS PRESENT, and that is the whole point. Writing the first
 * window of a day as a bare `2026-08-08` and only numbering the overflow would
 * make a day's first key depend on whether that day later overflowed — and
 * WhatsApp's HistorySync delivers messages into days that closed long ago. A
 * key that can be rewritten by a late arrival splits one conversation into two
 * documents with nobody able to see that it happened, which is exactly the
 * failure `chat_type` is kept out of the key to avoid.
 */
export const conversationWindowKeySchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}#[1-9]\d*$/, "must be YYYY-MM-DD#n with an ordinal from 1");
/**
 * An IANA time-zone name this runtime can actually resolve.
 *
 * Validated by construction rather than against a list: the list is the ICU
 * database's, and a name this build cannot resolve would otherwise silently
 * fall back to the host zone and put a message in the wrong day.
 */
export const conversationTimeZoneSchema = z
    .string()
    .min(1)
    .max(128)
    .refine((value) => {
    try {
        new Intl.DateTimeFormat("en-US", { timeZone: value });
        return true;
    }
    catch {
        return false;
    }
}, "must be a time zone this runtime can resolve");
/**
 * One message as the rollup sees it.
 *
 * `characterCount` is the message's contribution to the window, not its raw
 * body length — the rollup writes a per-message frame around every body, and
 * the cap is about the size of the finished document.
 */
export const conversationRollupMemberSchema = z
    .object({
    ...tenantOwnedShape,
    ...visibilityShape,
    sourceItemId: sourceItemIdSchema,
    /**
     * The canonical hash of the message document this member stands for. It is
     * what makes the digest notice an edit: a corrected message takes a new
     * canonical version, its hash changes, and the window is re-derived.
     */
    canonicalSha256: sha256Schema,
    /**
     * When the message was sent, as the provider reported it.
     *
     * Required, never nullable. A message whose send time is unknown cannot be
     * placed in a day, and quietly leaving it out of a window would be exactly
     * the silent skip this product does not have. Refusing it here forces the
     * caller to carry the gap explicitly instead.
     */
    sentAt: z.string().datetime({ offset: true }),
    characterCount: z.number().int().nonnegative(),
})
    .strict()
    .superRefine((value, context) => {
    enforceVisibilityInvariant(value, context);
});
/**
 * What names one rollup.
 *
 * `chatType` IS DELIBERATELY ABSENT. A Teams chat is `oneOnOne` until somebody
 * is added and then it is `group`, while its chat id never changes. Putting the
 * type in the key would break the identity at that moment and split one
 * conversation into two documents silently.
 *
 * `visibility` IS DELIBERATELY PRESENT. A chat with both `owner_private` and
 * `tenant_shared` messages produces two rollups, each composed only of the
 * messages its own readers may already open. A derivative may not widen the
 * visibility of its evidence, so the two are never blended into one stored
 * document; a reader entitled to both sees them merged at read time, under
 * their own authorization, and nothing is materialized from that merge.
 *
 * THE MAILBOX IS NOT A SEPARATE PART, and this is a claim the schema backs
 * rather than a simplification. `source_items` carries
 * `(tenant_id, connection_id, provider, provider_tenant_id, provider_account_id)`
 * as a composite foreign key into `connections`, so one connection has exactly
 * one provider account: the mailbox is functionally determined by
 * `connectionId`, which is already here. Adding it could not separate two
 * mailboxes that this key merges, because there are none — it would only be a
 * second source for one fact, free to drift from the first.
 */
export const conversationRollupIdentitySchema = z
    .object({
    ...tenantOwnedShape,
    ...visibilityShape,
    connectionId: connectionIdSchema,
    channel: conversationChannelSchema,
    /** WhatsApp's chat JID or the Teams chat id — the room, never a person. */
    chatContainerId: conversationKeyPartSchema,
    windowKey: conversationWindowKeySchema,
})
    .strict()
    .superRefine((value, context) => {
    enforceVisibilityInvariant(value, context);
});
/**
 * A whole window: what it is called, and exactly which messages it is made of.
 *
 * The refinements here are the fail-closed half of the design. A composition
 * that mixes tenants, visibility classes or owners is refused where it is built
 * rather than at the database, so a rollup can never be constructed that would
 * show a reader a message they may not open.
 */
export const conversationRollupCompositionSchema = z
    .object({
    identity: conversationRollupIdentitySchema,
    timeZone: conversationTimeZoneSchema,
    members: z.array(conversationRollupMemberSchema).min(1).max(10_000),
})
    .strict()
    .superRefine((value, context) => {
    const expectedDay = value.identity.windowKey.slice(0, 10);
    const seen = new Set();
    let previousOrder = "";
    value.members.forEach((member, index) => {
        if (member.tenantId !== value.identity.tenantId) {
            context.addIssue({
                code: "custom",
                path: ["members", index, "tenantId"],
                message: "Cross-tenant references are forbidden.",
            });
        }
        if (member.visibility !== value.identity.visibility) {
            context.addIssue({
                code: "custom",
                path: ["members", index, "visibility"],
                message: "A conversation rollup composes one visibility class only; a mixed conversation produces one rollup per class.",
            });
        }
        if (member.ownerMemberId !== value.identity.ownerMemberId) {
            context.addIssue({
                code: "custom",
                path: ["members", index, "ownerMemberId"],
                message: "A conversation rollup may not compose another member's private record.",
            });
        }
        if (seen.has(member.sourceItemId)) {
            context.addIssue({
                code: "custom",
                path: ["members", index, "sourceItemId"],
                message: "A message appears in its window exactly once.",
            });
        }
        seen.add(member.sourceItemId);
        if (tenantLocalDay(member.sentAt, value.timeZone) !== expectedDay) {
            context.addIssue({
                code: "custom",
                path: ["members", index, "sentAt"],
                message: "The message does not fall on this window's local day.",
            });
        }
        const order = memberOrderKey(member);
        if (index > 0 && order <= previousOrder) {
            context.addIssue({
                code: "custom",
                path: ["members", index],
                message: "Members are ordered by (sentAt, sourceItemId) with no ties.",
            });
        }
        previousOrder = order;
    });
});
function dayFormatterOf(timeZone) {
    return new Intl.DateTimeFormat("en-US", {
        timeZone,
        calendar: "gregory",
        numberingSystem: "latn",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    });
}
function formatDay(formatter, instant) {
    const parts = formatter.formatToParts(instant);
    const partOf = (type) => parts.find((part) => part.type === type)?.value ?? "";
    const year = partOf("year").padStart(4, "0");
    return `${year}-${partOf("month")}-${partOf("day")}`;
}
/**
 * Which local calendar day an instant falls on for a tenant.
 *
 * THE ZONE IS THE TENANT'S, NOT UTC, and the difference is not cosmetic. The
 * worker's default is UTC; Turkey is three hours ahead of it, so under a UTC
 * boundary every message sent between midnight and 03:00 lands in the previous
 * day's window and "what did we talk about last night" opens the wrong file.
 *
 * Assembled from `formatToParts` rather than from a formatted string so the
 * result cannot depend on a locale's date order or on its digits.
 */
export function tenantLocalDay(instantIso, timeZone) {
    const instant = new Date(instantIso);
    if (Number.isNaN(instant.getTime())) {
        throw new TypeError(`Not a valid instant: ${instantIso}`);
    }
    return formatDay(dayFormatterOf(timeZone), instant);
}
/** `YYYY-MM-DD#n` from its two parts. */
export function conversationWindowKeyOf(localDay, ordinal) {
    return conversationWindowKeySchema.parse(`${localDay}#${ordinal}`);
}
/**
 * The order two messages are read in, as one comparable string.
 *
 * `sentAt` alone is not an order: two messages of the same conversation can
 * carry the same millisecond, and an `ORDER BY` without a tie-break has already
 * cost this project a live flap. The source item id breaks it, and because it
 * is a UUID the result is total and stable.
 *
 * The instant is normalized to UTC first. `2026-08-08T00:00:00+03:00` and
 * `2026-08-07T21:00:00Z` are the same moment written two ways, and a digest
 * over the raw strings would call the same conversation two different things
 * depending on which spelling the provider happened to send.
 */
function memberOrderKey(member) {
    return `${utcInstantOf(member.sentAt)}${CONVERSATION_KEY_SEPARATOR}${member.sourceItemId}`;
}
function utcInstantOf(instantIso) {
    const instant = new Date(instantIso);
    if (Number.isNaN(instant.getTime())) {
        throw new TypeError(`Not a valid instant: ${instantIso}`);
    }
    return instant.toISOString();
}
/** Messages in the order a window reads them: `(sentAt, sourceItemId)`. */
export function orderConversationMembers(members) {
    return [...members].sort((left, right) => memberOrderKey(left) < memberOrderKey(right) ? -1 : 1);
}
/**
 * Cut an ordered conversation into windows: one per tenant-local day, split
 * further whenever the character cap would be passed.
 *
 * A message is never split. One message larger than the whole cap therefore
 * gets a window to itself and that window is over the cap — which is the honest
 * outcome: the alternative is a rollup that silently holds part of what someone
 * wrote.
 *
 * Deterministic in both directions. The same messages always produce the same
 * windows, and because the ordinal counts from the start of the day rather than
 * from the last split, a message arriving late into a closed day re-derives the
 * whole day rather than appending to whatever it last looked like.
 */
export function assignConversationWindows(members, options) {
    const timeZone = conversationTimeZoneSchema.parse(options.timeZone);
    const cap = options.characterCap ?? CONVERSATION_WINDOW_CHARACTER_CAP;
    if (!Number.isInteger(cap) || cap < 1) {
        throw new RangeError("The window character cap must be a positive integer.");
    }
    const formatter = dayFormatterOf(timeZone);
    const windows = [];
    let openDay = "";
    let openOrdinal = 0;
    let openCharacters = 0;
    let openMembers = [];
    const close = () => {
        if (openMembers.length === 0)
            return;
        windows.push({
            windowKey: conversationWindowKeyOf(openDay, openOrdinal),
            localDay: openDay,
            ordinal: openOrdinal,
            characterCount: openCharacters,
            members: openMembers,
        });
        openCharacters = 0;
        openMembers = [];
    };
    for (const member of orderConversationMembers(members)) {
        const localDay = formatDay(formatter, new Date(utcInstantOf(member.sentAt)));
        if (localDay !== openDay) {
            close();
            openDay = localDay;
            openOrdinal = 1;
        }
        else if (openMembers.length > 0 &&
            openCharacters + member.characterCount > cap) {
            close();
            openOrdinal += 1;
        }
        openMembers.push(member);
        openCharacters += member.characterCount;
    }
    close();
    return windows;
}
/**
 * A stable RFC-4122-shaped UUID over U+001F-joined parts.
 *
 * Deliberately not the `deterministicUuid` of `packages/parsers`: that one
 * hashes a JSON encoding of its arguments, and the conversation keys are
 * specified as a separator join. The two therefore produce different ids from
 * the same parts, which is why this is not exported — a caller reaching for a
 * generic derivation would eventually reach for the wrong one.
 */
async function conversationUuid(namespace, parts) {
    const digest = await sha256Hex([namespace, ...parts].join(CONVERSATION_KEY_SEPARATOR));
    const hex = digest.slice(0, 32).split("");
    hex[12] = "5";
    const variant = Number.parseInt(hex[16] ?? "0", 16);
    hex[16] = ((variant & 0x3) | 0x8).toString(16);
    const joined = hex.join("");
    return [
        joined.slice(0, 8),
        joined.slice(8, 12),
        joined.slice(12, 16),
        joined.slice(16, 20),
        joined.slice(20, 32),
    ].join("-");
}
function identityParts(identity) {
    return [
        identity.tenantId,
        identity.connectionId,
        identity.channel,
        identity.chatContainerId,
        identity.windowKey,
        identity.visibility,
    ];
}
/**
 * What the synthetic window source item calls itself to its connection.
 *
 * `source_items` is unique on `(tenant_id, connection_id, provider_item_id)`, so
 * the window key alone will not do: the same day of two different chats would
 * collide. The reference therefore carries the chat and the visibility class
 * alongside it, and opens with a literal tag so it can never collide with a real
 * provider item id arriving on the same connection.
 */
export function conversationWindowReference(identityInput) {
    const identity = conversationRollupIdentitySchema.parse(identityInput);
    return [
        "conversation-window",
        identity.channel,
        identity.chatContainerId,
        identity.windowKey,
        identity.visibility,
    ].join(CONVERSATION_KEY_SEPARATOR);
}
/** The synthetic source item a window's rollup document hangs from. */
export async function conversationWindowSourceItemId(identityInput) {
    const identity = conversationRollupIdentitySchema.parse(identityInput);
    return sourceItemIdSchema.parse(await conversationUuid(CONVERSATION_WINDOW_NAMESPACE, identityParts(identity)));
}
/** The rollup document of one window. */
export async function conversationRollupDocumentId(identityInput) {
    const identity = conversationRollupIdentitySchema.parse(identityInput);
    return documentIdSchema.parse(await conversationUuid(CONVERSATION_ROLLUP_NAMESPACE, identityParts(identity)));
}
/**
 * How complete a rollup claims to be.
 *
 * A rollup is a derived view of a scan that is itself incomplete for most of
 * its life, and the one failure this layer must never have is going quiet: a
 * window that is missing this morning's messages must not read as a window
 * where nothing was said. The three values are the same three the rest of the
 * product carries — a gap is `partial`, an unreadable scan state is `unknown`,
 * and only a window whose source is scanned past its own end is `complete`.
 */
export const conversationRollupCoverageStates = [
    "complete",
    "partial",
    "unknown",
];
export const conversationRollupCoverageStateSchema = z.enum(conversationRollupCoverageStates);
/**
 * The coverage a rollup declares in its own text.
 *
 * `asOf` IS THE SOURCE'S WATERMARK, NOT THE BUILD TIME. A rollup built at noon
 * from a scan that reached 09:00 is complete to 09:00 and to nothing later;
 * stamping it with its own clock would state a completeness the scan never
 * earned. That is why the field is nullable and why a null one is `unknown`
 * rather than "now": a window whose cursor cannot be read says so.
 */
export const conversationRollupCoverageSchema = z
    .object({
    state: conversationRollupCoverageStateSchema,
    asOf: z.string().datetime({ offset: true }).nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.state !== "unknown" && value.asOf === null) {
        context.addIssue({
            code: "custom",
            path: ["asOf"],
            message: "A rollup may only claim complete or partial coverage against a watermark it read.",
        });
    }
});
/**
 * How much of a machine reading a conversation may quote.
 *
 * 280 characters, measured rather than chosen (§7.2): the median reading that
 * clears the OCR gate is 1 168 characters while a whole chat message is 316, so
 * an unbounded quote would make one photo weigh 3,7 messages and turn a
 * conversation into a document dump. At 280 the photo weighs about one message.
 * The measured price is that 96,3 % of readings are cut — which is why the cut
 * is always stated and the full document always cited.
 */
export const CONVERSATION_ROLLUP_QUOTE_CHARACTER_LIMIT = 280;
/**
 * Why a photo carries no quote, when it carries none.
 *
 * Every one of these says "this was not read", never "there is nothing here".
 * A picture whose reading failed and a picture that holds no words are not the
 * same fact, and collapsing them would be exactly the silent skip this product
 * does not have.
 *
 * `not_published` is the narrow honest case: a reading exists and cleared the
 * gate, but its document is not live, so there is nothing citable to point at
 * and the quote would have no evidence behind it.
 */
export const conversationRollupUnreadReasons = [
    "not_attempted",
    "in_progress",
    "unsupported",
    "failed",
    "inaccessible",
    "not_published",
];
export const conversationRollupUnreadReasonSchema = z.enum(conversationRollupUnreadReasons);
const readingConfidenceSchema = z.number().min(0).max(1);
/**
 * What a machine reading of one photo amounts to, in the four states it can
 * actually be in.
 *
 * THE THRESHOLD IS NOT A NUMBER HERE, and that is deliberate. The gate is two
 * layers — the builder's own floor (`OCR_DERIVED_MIN_CONFIDENCE`) and the
 * deployed `OBSINA_MEDIA_OCR_CONFIDENCE_THRESHOLD`, which is configured per
 * environment and is the binding one because it is never below the floor. A
 * rollup that re-applied a hard-coded number would disagree with whichever of
 * the two actually ran. So this shape carries the *outcome* of that gate, read
 * back from what the pipeline persisted, and the renderer never compares a
 * confidence to anything.
 *
 * `below_threshold` carries no text on purpose (§7.3): a reading the product
 * trusted too little to turn into a document must not reappear in a rollup, or
 * the gate would be passable through the back door. The confidence is written
 * so the shortfall stays visible instead of looking like an absence.
 */
export const conversationRollupOcrReadingSchema = z.discriminatedUnion("state", [
    z
        .object({
        state: z.literal("quoted"),
        /** The `ocr_derived` document the quote is cut from and cites. */
        documentId: documentIdSchema,
        confidence: readingConfidenceSchema,
        /**
         * The whole reading, as its published document holds it. The quote is
         * cut from it at render time rather than upstream, so one place decides
         * where the cut falls and the cut can never drift from the count that
         * announces it.
         */
        text: z
            .string()
            .min(1)
            .max(250_000)
            .refine((value) => normalizedQuoteText(value).length > 0, "a quoted reading must carry at least one readable character"),
    })
        .strict(),
    z
        .object({
        state: z.literal("below_threshold"),
        confidence: readingConfidenceSchema,
    })
        .strict(),
    z.object({ state: z.literal("no_text") }).strict(),
    z
        .object({
        state: z.literal("not_read"),
        reason: conversationRollupUnreadReasonSchema,
    })
        .strict(),
]);
/**
 * A media message's place in the rollup.
 *
 * The rollup holds the message's POSITION, a bounded quote and a pointer — never
 * the reading itself: the full text, its confidence and its evidence stay in the
 * `ocr_derived` document, which is searchable and citable on its own terms. The
 * rollup reads that document and never writes to it.
 *
 * The citation lives inside the `quoted` reading rather than beside it, so a
 * media message cannot be built that names a document it did not quote, or
 * quotes one it cannot name.
 */
export const conversationRollupMediaSchema = z
    .object({
    /** The provider's media type, e.g. `image` or `image/jpeg`. */
    mediaLabel: conversationKeyPartSchema.max(128),
    reading: conversationRollupOcrReadingSchema,
})
    .strict();
/**
 * A reading flattened to one quotable line.
 *
 * OCR text arrives with page breaks, hard-wrapped lines and the occasional
 * control byte. Left alone, any of the three would escape the blockquote the
 * rollup puts it in and forge a heading or a citation line of its own — the
 * reading is untrusted content, and it is rendered into a document a human
 * reads as structure. Collapsing every whitespace and control run to one space
 * removes the vector and makes the quote's length mean what it says.
 */
function normalizedQuoteText(value) {
    return (value
        // eslint-disable-next-line no-control-regex -- folding C0/C1 is the point
        .replace(/[\u0000-\u001f\u007f-\u009f]/gu, " ")
        .replace(/\s+/gu, " ")
        .trim());
}
/**
 * Cut a reading down to a quote, at a word boundary, the same way every time.
 *
 * DETERMINISM IS A HARD REQUIREMENT, not a nicety: the quote is part of the
 * rollup's text, the text is part of what the window republishes, and a cut
 * that wandered by a character would make the same window look changed on every
 * rebuild. Nothing here depends on a locale, a clock or a segmenter.
 *
 * The boundary is the last space inside the budget. A single word longer than
 * the whole budget has no boundary to fall back on and is cut where the budget
 * ends — the honest outcome, and still the same one every time.
 *
 * The omitted count is measured against the folded reading, so it answers the
 * question the reader is actually asking: how much of *this* am I not seeing.
 */
export function conversationRollupQuoteOf(text, limit = CONVERSATION_ROLLUP_QUOTE_CHARACTER_LIMIT) {
    if (!Number.isInteger(limit) || limit < 1) {
        throw new RangeError("The quote character limit must be a positive integer.");
    }
    const folded = normalizedQuoteText(text);
    const points = Array.from(folded);
    if (points.length <= limit) {
        return { quote: folded, omittedCharacterCount: 0 };
    }
    const head = points.slice(0, limit).join("");
    const boundary = head.lastIndexOf(" ");
    const quote = (boundary > 0 ? head.slice(0, boundary) : head).trimEnd();
    return {
        quote,
        omittedCharacterCount: points.length - Array.from(quote).length,
    };
}
export const conversationMessageDirections = ["inbound", "outbound"];
export const conversationMessageDirectionSchema = z.enum(conversationMessageDirections);
/**
 * One message as the rollup renders it.
 *
 * `body` is the message's own words and nothing else — the canonical document's
 * front matter and its structural detail lines are the parser's frame around
 * them, and repeating that frame once per message would make a day of chat read
 * like a stack of file headers. A message with no body at all (a bare photo)
 * carries `null` and is rendered by its media line, never dropped.
 */
export const conversationRollupRenderMemberSchema = z
    .object({
    sentAt: z.string().datetime({ offset: true }),
    direction: conversationMessageDirectionSchema.nullable(),
    body: z.string().min(1).max(200_000).nullable(),
    media: conversationRollupMediaSchema.nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.body === null && value.media === null) {
        context.addIssue({
            code: "custom",
            path: ["body"],
            message: "A rendered message carries a body, a media placeholder, or both.",
        });
    }
});
const channelLabels = {
    whatsapp: "WhatsApp",
    teams: "Microsoft Teams",
};
const directionLabels = {
    inbound: "gelen",
    outbound: "giden",
};
function clockFormatterOf(timeZone) {
    return new Intl.DateTimeFormat("en-US", {
        timeZone,
        hourCycle: "h23",
        hour: "2-digit",
        minute: "2-digit",
        numberingSystem: "latn",
    });
}
/** `HH:MM` in the tenant's zone, assembled from parts so no locale can reorder it. */
function tenantLocalClock(formatter, instantIso) {
    const parts = formatter.formatToParts(new Date(utcInstantOf(instantIso)));
    const partOf = (type) => parts.find((part) => part.type === type)?.value ?? "";
    return `${partOf("hour")}:${partOf("minute")}`;
}
/**
 * The coverage statement a rollup opens with.
 *
 * Deliberately a sentence and not a status word: the reader of a chat log is
 * not reading a dashboard, and "partial" on its own tells them nothing about
 * which end of the day is missing. The visibility sentence is the general form
 * §6.3 settled on — it never says how many records were withheld, because that
 * count is itself a leak.
 */
function coverageLines(coverage) {
    const visibility = "- Bu görünüm yalnız sizin erişebildiğiniz kayıtlardan türetilmiştir.";
    if (coverage.state === "unknown" || coverage.asOf === null) {
        return [
            "- Kapsam: bu pencerenin eksiksizliği bilinmiyor — kaynak tarama durumu okunamadı.",
            visibility,
        ];
    }
    const asOf = utcInstantOf(coverage.asOf);
    return [
        coverage.state === "complete"
            ? `- Kapsam: bu pencere ${asOf} itibarıyla eksiksiz.`
            : `- Kapsam: bu pencere ${asOf} itibarıyla eksik — kaynak tarama bu andan sonrasını henüz kapsamıyor.`,
        visibility,
    ];
}
/**
 * A confidence as the rollup writes it: two decimals, Turkish comma, and no
 * locale anywhere near it.
 *
 * `toFixed` is locale-independent and the comma is substituted afterwards, so
 * the same reading renders identically on a CI runner and on a Turkish laptop.
 * A number formatter would not — and this string goes into a document whose
 * hash is a compare-and-set token.
 */
function renderedConfidence(confidence) {
    return (Math.round(confidence * 100) / 100).toFixed(2).replace(".", ",");
}
/**
 * The sentence each unread reason gets.
 *
 * Six distinct sentences rather than one shared "no reading": a picture nobody
 * looked at, a picture the reader could not fetch and a file type this pipeline
 * does not read are three different facts about the product, and a reader
 * chasing a missing number needs to know which one they hit. None of them says
 * the photo held nothing.
 */
const unreadReasonLines = {
    not_attempted: "↳ makine okuması yapılmadı — bu görsel okunmadı",
    in_progress: "↳ makine okuması sürüyor — bu görsel henüz okunmadı",
    unsupported: "↳ bu tür makine okumasıyla okunmuyor — içeriği okunmadı",
    failed: "↳ makine okuması başarısız oldu — bu görsel okunmadı",
    inaccessible: "↳ medyaya erişilemedi — bu görsel okunmadı",
    not_published: "↳ makine okuması belge olarak henüz yayınlanmadı — burada gösterilemiyor",
};
/**
 * What a photo contributes below its own heading, in each of the four states.
 *
 * The states never share a phrasing. "Read it and it says this", "read it but
 * trusted it too little to show you", "read it and it holds no words" and "did
 * not read it" are four different claims, and a reader who cannot tell them
 * apart cannot tell an empty picture from an unexamined one — which is the one
 * confusion this product refuses to create.
 *
 * Only the quoted state cites a document, because only the quoted state has
 * one: the other three exist precisely because no `ocr_derived` document was
 * published, and inventing a reference to a row that is not there would send
 * the reader after evidence that does not exist.
 */
function readingLines(reading) {
    if (reading.state === "below_threshold") {
        return [
            `↳ makine okuması güven eşiğinin altında kaldı (güven ${renderedConfidence(reading.confidence)}) — metin gösterilmiyor`,
        ];
    }
    if (reading.state === "no_text") {
        return ["↳ makine okundu, okunabilir metin bulunamadı"];
    }
    if (reading.state === "not_read") {
        return [unreadReasonLines[reading.reason]];
    }
    const { quote, omittedCharacterCount } = conversationRollupQuoteOf(reading.text);
    const tail = omittedCharacterCount === 0
        ? ""
        : ` … (kesildi — ${omittedCharacterCount} karakter daha)`;
    return [
        `> (makine okuması · güven ${renderedConfidence(reading.confidence)}) ${quote}${tail}`,
        `↳ tam metin: ocr_derived/${reading.documentId}`,
    ];
}
/**
 * The header block of one window: what it is, how far it reaches, and whose
 * records it was built from.
 */
export function renderConversationRollupHeader(input) {
    const windowKey = conversationWindowKeySchema.parse(input.windowKey);
    const coverage = conversationRollupCoverageSchema.parse(input.coverage);
    const channel = conversationChannelSchema.parse(input.channel);
    const [localDay = "", ordinal = ""] = windowKey.split("#");
    return [
        `# Konuşma · ${localDay} (bölüm ${ordinal})`,
        "",
        `- Kanal: ${channelLabels[channel]}`,
        ...coverageLines(coverage),
        "",
        "",
    ].join("\n");
}
/**
 * One message's block, frame included.
 *
 * This is also the unit the window cap is measured in: `assignConversationWindows`
 * is fed the length of exactly this string, so the cap bounds the document that
 * is actually written rather than the sum of the raw bodies inside it.
 */
export function renderConversationRollupMessage(memberInput, timeZoneInput) {
    const member = conversationRollupRenderMemberSchema.parse(memberInput);
    const timeZone = conversationTimeZoneSchema.parse(timeZoneInput);
    const heading = [
        tenantLocalClock(clockFormatterOf(timeZone), member.sentAt),
        member.direction === null ? null : directionLabels[member.direction],
        member.media === null ? null : `[görsel: ${member.media.mediaLabel}]`,
    ]
        .filter((part) => part !== null)
        .join(" · ");
    const lines = [`### ${heading}`];
    if (member.body !== null)
        lines.push(member.body);
    if (member.media !== null) {
        lines.push(...readingLines(member.media.reading));
    }
    return `${lines.join("\n")}\n\n`;
}
/**
 * The digest of everything a window is made of.
 *
 * It is both the idempotency key and the compare-and-set token of a rollup
 * refresh: a job that runs twice recomputes the same digest and writes nothing,
 * and two jobs that run at once publish under `where current_digest = <read>`,
 * so the loser retries, recomputes the same digest and falls to the same no-op.
 *
 * Each member contributes its source item, its canonical hash, its visibility
 * class and its send instant. The canonical hash is what makes an edited or
 * withdrawn message move the digest; the visibility class is in it so that a
 * message being reclassified re-derives the window rather than leaving a
 * stale composition in place.
 *
 * The parts are joined flat rather than record-by-record because every one of
 * them has a fixed shape — a UUID, a 64-character hex digest, a controlled
 * label and an ISO instant — and none of them can contain the separator. There
 * is therefore no arrangement of members that produces the same string as a
 * different arrangement.
 */
export async function conversationRollupContentDigest(members) {
    const parsed = members.map((member) => conversationRollupMemberSchema.parse(member));
    const ordered = orderConversationMembers(parsed);
    const parts = [];
    for (const member of ordered) {
        parts.push(member.sourceItemId, member.canonicalSha256, member.visibility, utcInstantOf(member.sentAt));
    }
    return sha256Hex(parts.join(CONVERSATION_KEY_SEPARATOR));
}
//# sourceMappingURL=conversation-rollup.js.map